import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Ban, Upload, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

import { BACKGROUND_IMAGE_CONTENT_TYPES } from '../../lib/backgroundImage';
import { cn } from '../../lib/utils';
import {
  getWorkspaceBackgroundHistory,
  rememberWorkspaceBackground,
  WORKSPACE_BACKGROUND_HISTORY_LIMIT,
  type WorkspaceBackgroundHistoryEntry,
} from '../../lib/workspaceBackgroundHistory';
import { dismissWorkspaceBackgroundPicker, isWorkspaceBackgroundPickerDismissed } from '../../lib/workspaceBackgroundPickerDismissal';
import { WORKSPACE_BACKGROUND_IMAGE_PRESETS, type WorkspaceBackgroundPreset } from '../../lib/workspaceBackgroundPresets';

import SpinnerIcon from '../../../assets/images/spinner.svg';

import useBackgroundImageUpload from '../../hooks/useBackgroundImageUpload';
import useWorkspaceBackgroundImageMutation from '../../hooks/useWorkspaceBackgroundImageMutation';
import { Tooltip } from './Tooltip';

interface BackgroundImageResource {
  id?: string;
  [key: string]: unknown;
}

interface WorkspaceResource {
  id?: string;
  background_image?: BackgroundImageResource | null;
  background_image_preset?: WorkspaceBackgroundPreset | null;
  background_image_url?: string | null;
}

type BackgroundChoice = WorkspaceBackgroundPreset | 'none' | 'upload' | 'image';

export interface BackgroundChoiceTileProps {
  accessibleLabel: string;
  busy?: boolean;
  children?: React.ReactNode;
  className?: string;
  disabled: boolean;
  imageUrl?: string | null;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  /** Tiny inline preview (a thumbhash data URL) painted under the real image while it downloads. */
  placeholderUrl?: string | null;
  selected: boolean;
  tooltip?: string | null;
}

export function BackgroundChoiceTile({
  accessibleLabel,
  busy = false,
  children,
  className,
  disabled,
  imageUrl = null,
  onClick,
  placeholderUrl = null,
  selected,
  tooltip = null,
}: BackgroundChoiceTileProps) {
  const tile = (
    <button
      type="button"
      data-app-region="none"
      aria-label={accessibleLabel}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'relative flex h-19 w-full min-w-0 cursor-pointer items-center justify-center overflow-hidden rounded-lg border-[0.5px] border-white bg-gray-50 text-gray-400 transition-[background-color,filter] disabled:cursor-not-allowed disabled:opacity-70 dark:border-white/10 dark:bg-neutral-800 dark:text-neutral-400',
        // everything dims on hover: wallpapers noticeably, gray placeholder tiles just a touch —
        // a brightness step reads subtler than any gray-to-gray bg swap
        imageUrl ? 'hover:brightness-90' : 'hover:brightness-95',
        className,
        // selection: blue ring outside a 1px white separator border, so it pops on any wallpaper
        selected && 'border border-white ring-2 ring-idle-blue dark:border-neutral-900',
      )}
    >
      {placeholderUrl ? (
        <span
          aria-hidden="true"
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${placeholderUrl})` }}
        />
      ) : null}
      {imageUrl ? <img src={imageUrl} alt="" className="relative size-full object-cover" draggable="false" /> : children}
      {busy ? (
        <span className="absolute inset-0 flex items-center justify-center bg-black/30 text-white">
          <SpinnerIcon className="size-4 animate-spin" />
        </span>
      ) : null}
    </button>
  );

  // The active wallpaper gets no tooltip — "Set as wallpaper" is a no-op there
  if (!tooltip || selected) return tile;

  return (
    <Tooltip label={tooltip} placement="top" className="flex w-full min-w-0">
      {tile}
    </Tooltip>
  );
}

interface WorkspacePresetTileProps {
  busy: boolean;
  disabled: boolean;
  onSelect: (preset: WorkspaceBackgroundPreset) => void;
  preset: WorkspaceBackgroundPreset;
  selected: boolean;
}

function WorkspacePresetTile({ busy, disabled, onSelect, preset, selected }: WorkspacePresetTileProps) {
  const config = WORKSPACE_BACKGROUND_IMAGE_PRESETS[preset];
  const handleClick = useCallback(() => {
    onSelect(preset);
  }, [onSelect, preset]);

  if (!config) return null;

  return (
    <BackgroundChoiceTile
      accessibleLabel={`${config.label} background`}
      busy={busy}
      disabled={disabled}
      imageUrl={config.url}
      onClick={handleClick}
      selected={selected}
      tooltip="Set as wallpaper"
    />
  );
}

interface WorkspaceHistoryTileProps {
  busy: boolean;
  disabled: boolean;
  entry: WorkspaceBackgroundHistoryEntry;
  onSelect: (entry: WorkspaceBackgroundHistoryEntry) => void;
  selected: boolean;
}

function WorkspaceHistoryTile({ busy, disabled, entry, onSelect, selected }: WorkspaceHistoryTileProps) {
  const handleClick = useCallback(() => {
    onSelect(entry);
  }, [entry, onSelect]);

  return (
    <BackgroundChoiceTile
      accessibleLabel="Previously uploaded background"
      busy={busy}
      disabled={disabled}
      imageUrl={entry.url}
      onClick={handleClick}
      selected={selected}
      tooltip="Set as wallpaper"
    />
  );
}

interface WorkspaceBackgroundPickerProps {
  className?: string;
  dismissible?: boolean;
  // When false the card reports dismissal but stays mounted, leaving visibility to the
  // caller. The onboarding deck needs this: self-unmounting kills its exit animation.
  hideOnDismiss?: boolean;
  // Fired once a background actually lands. The onboarding deck retires the card on this —
  // picking a wallpaper *is* acting on the card, so it shouldn't need dismissing too.
  onChoiceApplied?: () => void;
  onDismiss?: () => void;
  profileId?: string | null;
  // Override the save path for resources whose background isn't written via PATCH
  // /workspaces (e.g. direct messages, which write through their own endpoint).
  // Both must be provided together; the resource shape stays workspace-like.
  saveAttachment?: (attachmentId?: string | null) => Promise<BackgroundImageResource | null>;
  savePreset?: (preset?: WorkspaceBackgroundPreset | null) => Promise<unknown>;
  sessionKeyScope?: string;
  workspace: WorkspaceResource;
}

function WorkspaceBackgroundPicker({
  className,
  dismissible = true,
  hideOnDismiss = true,
  onChoiceApplied,
  onDismiss,
  profileId,
  saveAttachment: saveAttachmentOverride,
  savePreset: savePresetOverride,
  sessionKeyScope = 'end-of-messages-background-picker',
  workspace,
}: WorkspaceBackgroundPickerProps) {
  const workspaceId = workspace.id ? String(workspace.id) : null;
  const [isDismissed, setIsDismissed] = useState(
    () => hideOnDismiss && dismissible && isWorkspaceBackgroundPickerDismissed(workspaceId, profileId),
  );
  const [pendingChoice, setPendingChoice] = useState<Exclude<BackgroundChoice, 'upload' | 'image'> | null>(null);
  const [pendingImageId, setPendingImageId] = useState<string | null>(null);
  const [history, setHistory] = useState<WorkspaceBackgroundHistoryEntry[]>(() => getWorkspaceBackgroundHistory(workspaceId));
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const workspaceMutation = useWorkspaceBackgroundImageMutation(workspaceId);
  const saveAttachment = saveAttachmentOverride || workspaceMutation.saveAttachment;
  const savePreset = savePresetOverride || workspaceMutation.savePreset;
  const customBackgroundImage = workspace.background_image_preset ? null : workspace.background_image;
  const customBackgroundImageUrl = workspace.background_image_preset ? null : workspace.background_image_url;
  const {
    canRetrySave,
    clearError: clearUploadError,
    displayedImageUrl,
    error: uploadError,
    isBusy: isUploadBusy,
    rejectFile,
    retrySave,
    uploadFile,
  } = useBackgroundImageUpload({
    backgroundImage: customBackgroundImage,
    backgroundImageUrl: customBackgroundImageUrl,
    uploadWorkspaceId: workspaceId,
    sessionKeyScope,
    resetKey: `${workspaceId || ''}:${workspace.background_image?.id || ''}:${workspace.background_image_preset || ''}`,
    externallyDisabled: !workspaceId,
    contentTypes: BACKGROUND_IMAGE_CONTENT_TYPES,
    onSave: saveAttachment,
  });
  const activeImageId = !workspace.background_image_preset && workspace.background_image?.id ? String(workspace.background_image.id) : null;
  const actualChoice = useMemo<BackgroundChoice>(() => {
    if (workspace.background_image_preset) return workspace.background_image_preset;
    if (workspace.background_image || workspace.background_image_url) return 'image';
    return 'none';
  }, [workspace.background_image, workspace.background_image_preset, workspace.background_image_url]);
  const isDirectMutationPending = pendingChoice !== null || pendingImageId !== null;
  const isBusy = isDirectMutationPending || isUploadBusy;
  const isUploadInFlight = isUploadBusy || canRetrySave;
  const displayedChoice = pendingChoice || (isUploadInFlight ? 'upload' : pendingImageId ? 'image' : actualChoice);
  // Which history tile carries the selection ring while 'image' is the displayed choice
  const displayedImageId = pendingImageId || activeImageId;
  const displayedError = selectionError || uploadError;
  // While the provisional upload tile occupies slot 1, keep the row at one grid line
  const visibleHistory =
    isUploadInFlight && displayedImageUrl ? history.slice(0, WORKSPACE_BACKGROUND_HISTORY_LIMIT - 1) : history;

  useEffect(() => {
    setIsDismissed(hideOnDismiss && dismissible && isWorkspaceBackgroundPickerDismissed(workspaceId, profileId));
  }, [dismissible, hideOnDismiss, profileId, workspaceId]);

  useEffect(() => {
    setHistory(getWorkspaceBackgroundHistory(workspaceId));
  }, [workspaceId]);

  // Any custom background reaching the workspace record (fresh upload or pre-existing)
  // lands in slot 1 of the history row; oldest entries fall off past one row.
  useEffect(() => {
    if (!workspaceId || !activeImageId || !customBackgroundImageUrl) return;
    setHistory(rememberWorkspaceBackground(workspaceId, { id: activeImageId, url: customBackgroundImageUrl }));
  }, [activeImageId, customBackgroundImageUrl, workspaceId]);

  const selectPreset = useCallback(
    async (preset: WorkspaceBackgroundPreset | null) => {
      const nextChoice = preset || 'none';
      if (isBusy || actualChoice === nextChoice) return;

      setPendingChoice(nextChoice);
      setSelectionError(null);
      clearUploadError();

      try {
        await savePreset(preset);
        onChoiceApplied?.();
      } catch {
        setSelectionError('Unable to save background image.');
      } finally {
        setPendingChoice(null);
      }
    },
    [actualChoice, clearUploadError, isBusy, onChoiceApplied, savePreset],
  );

  const handleNoneClick = useCallback(() => {
    selectPreset(null);
  }, [selectPreset]);

  const handleHistorySelect = useCallback(
    async (entry: WorkspaceBackgroundHistoryEntry) => {
      if (isBusy || activeImageId === entry.id) return;

      setPendingImageId(entry.id);
      setSelectionError(null);
      clearUploadError();

      try {
        await saveAttachment(entry.id);
        onChoiceApplied?.();
      } catch {
        setSelectionError('Unable to save background image.');
      } finally {
        setPendingImageId(null);
      }
    },
    [activeImageId, clearUploadError, isBusy, onChoiceApplied, saveAttachment],
  );

  const handlePresetSelect = useCallback(
    (preset: WorkspaceBackgroundPreset) => {
      selectPreset(preset);
    },
    [selectPreset],
  );

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      setSelectionError(null);
      uploadFile(acceptedFiles[0]);
    },
    [uploadFile],
  );
  const dropzoneAccept = useMemo(() => Object.fromEntries(BACKGROUND_IMAGE_CONTENT_TYPES.map((type) => [type, []])), []);
  const { getInputProps, open: openFilePicker } = useDropzone({
    accept: dropzoneAccept,
    disabled: isBusy,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop: handleDrop,
    onDropRejected: rejectFile,
  });

  const handleUploadClick = useCallback(() => {
    if (isBusy) return;
    setSelectionError(null);

    if (canRetrySave) {
      retrySave();
      return;
    }

    openFilePicker();
  }, [canRetrySave, isBusy, openFilePicker, retrySave]);

  const handleDismiss = useCallback(() => {
    if (!dismissible || isBusy) return;

    dismissWorkspaceBackgroundPicker(workspaceId, profileId);
    if (hideOnDismiss) setIsDismissed(true);
    onDismiss?.();
  }, [dismissible, hideOnDismiss, isBusy, onDismiss, profileId, workspaceId]);

  if (dismissible && isDismissed) return null;

  return (
    <div className={cn('w-full max-w-118', className)}>
      <section className="flex w-full flex-col overflow-hidden rounded-2xl border-[0.5px] border-white bg-gray-100 shadow-[0px_4px_20px_0px_rgba(0,0,0,0.16)] dark:border-white/10 dark:bg-neutral-900">
        <div className="flex w-full items-center justify-between px-4 pt-4">
          <h2 className="truncate text-sm/5 font-medium text-black dark:text-neutral-100">Background Image</h2>
          {dismissible ? (
            <button
              type="button"
              data-app-region="none"
              aria-label="Dismiss background image picker"
              disabled={isBusy}
              onClick={handleDismiss}
              className="flex size-6 cursor-pointer items-center justify-center text-gray-500 hover:text-black disabled:cursor-not-allowed disabled:opacity-40 dark:text-neutral-400 dark:hover:text-neutral-100"
            >
              <X size={16} strokeWidth={1.5} absoluteStrokeWidth />
            </button>
          ) : null}
        </div>

        <div className="grid w-full grid-cols-4 gap-2 p-4">
          <BackgroundChoiceTile
            accessibleLabel="No background"
            busy={pendingChoice === 'none'}
            // white surface needs a gentler dim than the gray tiles' brightness-95
            className={displayedChoice === 'none' ? undefined : 'border-gray-200 bg-white hover:brightness-[0.965]'}
            disabled={isBusy}
            onClick={handleNoneClick}
            selected={displayedChoice === 'none'}
            tooltip="Set as wallpaper"
          >
            <Ban size={18} strokeWidth={1.5} absoluteStrokeWidth />
          </BackgroundChoiceTile>
          <WorkspacePresetTile
            preset="blue"
            busy={pendingChoice === 'blue'}
            disabled={isBusy}
            onSelect={handlePresetSelect}
            selected={displayedChoice === 'blue'}
          />
          <WorkspacePresetTile
            preset="green"
            busy={pendingChoice === 'green'}
            disabled={isBusy}
            onSelect={handlePresetSelect}
            selected={displayedChoice === 'green'}
          />
          <div className="min-w-0">
            <input {...getInputProps()} />
            <BackgroundChoiceTile
              accessibleLabel="Upload custom background"
              busy={false}
              className="bg-gray-200"
              disabled={isBusy && !canRetrySave}
              onClick={handleUploadClick}
              selected={false}
              tooltip="Upload wallpaper"
            >
              <Upload size={18} strokeWidth={1.5} absoluteStrokeWidth />
            </BackgroundChoiceTile>
          </div>

          {/* Row 2: previously uploaded images, newest first, capped at one row (4x2 grid max).
              An in-flight upload previews in its destination slot at the front of the row. */}
          {isUploadInFlight && displayedImageUrl ? (
            <BackgroundChoiceTile
              accessibleLabel="Uploading background"
              busy={isUploadBusy}
              disabled={isBusy && !canRetrySave}
              imageUrl={displayedImageUrl}
              onClick={handleUploadClick}
              selected
            />
          ) : null}
          {visibleHistory.map((entry) => (
            <WorkspaceHistoryTile
              key={entry.id}
              entry={entry}
              busy={pendingImageId === entry.id}
              disabled={isBusy}
              selected={displayedChoice === 'image' && displayedImageId === entry.id}
              onSelect={handleHistorySelect}
            />
          ))}
        </div>

        {displayedError ? (
          <p className="h-8 shrink-0 truncate px-4 pb-4 text-xs text-red-700 dark:text-red-300">{displayedError}</p>
        ) : null}
      </section>
    </div>
  );
}

export default WorkspaceBackgroundPicker;
